import { ThreadTranslationEditorVMConfig } from 'components/editors/threadTranslation/threadTranslation';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import DiscussionTranslationManager from 'managers/discussion/translation';
import { IWCCStorageManager } from 'managers/iStorage';
import { TopicThread } from 'models/topicThread';
import './content.html';
import './content.scss';

components.preload('wcc-content', 'thread-translation-editor');

export interface CommentResponseVMConfig {
    comment: SubscribableOrNullableValue<TopicThread>
    compact: SubscribableOrNullableValue<boolean>
}

@injectable()
export default class CommentResponseVM {
    private comment: Subscribable<TopicThread | undefined>

    labels = labels
    translationLabel = labels.LanguageTranslationLabel.replace("{0}", settings.translationLanguageName ?? '').trim();

    topicId = ko.pureComputed(() => this.comment()?.topicId())

    content = ko.pureComputed(() => this.comment()?.content() ?? '')
    hasContent = ko.pureComputed(() => this.content().length > 0)

    translatedContent = ko.pureComputed(() => this.comment()?.translatedContent() ?? '')
    isTranslatedContentShown: Subscribable<boolean>

    attachments = ko.pureComputed(() => this.comment()?.attachments() ?? [])
    cardsScoreAnswers = ko.pureComputed(() => this.comment()?.cardsScoreAnswers() ?? [])

    translationEditorConfig: ThreadTranslationEditorVMConfig
    isTranslationEditorShown = ko.observable(false)

    isAllowedToEdit: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: CommentResponseVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager
    ) {
        this.comment = ko.flattenComputed(config.comment);

        const translationManager = storage.get(DiscussionTranslationManager, { discussionId: settings.discussionId });
        const isTranslationAllowed = translationManager.pluck('isAllowedToShow', false);

        this.isTranslatedContentShown = ko.pureComputed(() => isTranslationAllowed() && this.translatedContent().length > 0);

        this.translationEditorConfig = {
            thread: this.comment,
            save: () => this.isTranslationEditorShown(false),
            cancel: () => this.isTranslationEditorShown(false)
        }

        const userManager = storage.get(CurrentUserManager, { discussionId: settings.discussionId });
        const user = userManager.pluck('person');
        this.isAllowedToEdit = user.pluck('isModerator', false);

        const isCompact = ko.flattenComputed(config.compact, false);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'comment-content': true,
                'comment-content--compact': isCompact
            }
        }, this);
    }

    editTranslation = () => {
        this.isTranslationEditorShown(true);
    }
}